"use client";

import { useEffect, useRef, useState } from "react";
import type { PluginListenerHandle } from "@capacitor/core";
import type { LucideIcon } from "lucide-react";
import { LoaderCircle, Mic, Plus, Send, Square, X } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import {
  GlassBar,
  type GlassBarComposer,
  type GlassBarConfig,
  type GlassBarMic,
  type GlassBarMode,
  type GlassBarTab,
  type Glyph,
} from "@/lib/native/glass-bar";
import { glyphFromSvg } from "@/lib/native/svg-glyph";
import { askMarkGlyph } from "@/lib/native/ask-mark";
import { nativeBarHeld, onNativeBarRestore } from "@/lib/ui/chrome-cover";

/**
 * The native glass bar trial (PR 4, docs/liquid-glass-native-bar-plan.md):
 * MobileNav's state, handed to `MuckaGlassBarPlugin`, and its taps handed back.
 *
 * Rendered by MobileNav only when `useUiGlassNative()` is true, and only where
 * MobileNav would draw a bar at all — so every reason the web bar disappears
 * (an intro sheet, the offline surface, a desktop width) unmounts this, and
 * unmounting hides the native bar. There is no second list of those reasons.
 *
 * 🚨 This decides nothing. Which tabs, which one is current, what the round
 * button means — all of it arrives as props from MobileNav, which keeps
 * drawing its own (hidden) bar from the same state. The handlers are
 * MobileNav's own, so a tab tap on the native bar runs exactly the offline
 * interception, dead-nav watch and modal closing a web tap does.
 */

export interface NativeTab {
  key: string;
  label: string;
  icon: LucideIcon;
}

interface Props {
  left: NativeTab[];
  /** `null` is the spacer column that keeps the orb centred on the web. */
  right: (NativeTab | null)[];
  ask: boolean;
  active: string | null;
  dark: boolean;
  mode: GlassBarMode;
  placeholder: string;
  mic: GlassBarMic;
  attachOpen: boolean;
  dimmed: boolean;
  foldIcon: LucideIcon;
  /** False while the full conversation shows its own X. */
  fold: boolean;
  /** The dark chat card is behind the composer (TKT-1619). */
  onDark: boolean;
  draft: string;
  onTab: (key: string) => void;
  onAsk: () => void;
  onFold: () => void;
  onAttach: () => void;
  onMic: () => void;
  onSubmit: (text: string) => void;
  onDraft: (text: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

const COMPOSER_ICONS: [string, LucideIcon, boolean?][] = [
  ["plus", Plus],
  ["x", X],
  ["mic", Mic],
  ["send", Send],
  ["stop", Square, true],
  ["loader", LoaderCircle],
];

export default function NativeGlassBar(props: Props) {
  const glyphBox = useRef<HTMLDivElement | null>(null);
  const handlers = useRef(props);
  // What the native field last told us it holds, so a draft we are only
  // echoing back is never pushed to it (that would move the caret mid-word).
  const nativeDraft = useRef("");

  useEffect(() => {
    handlers.current = props;
  });

  const glyph = (name: string): Glyph | undefined => {
    const svg = glyphBox.current?.querySelector<SVGSVGElement>(`[data-glyph="${name}"] svg`);
    return svg ? glyphFromSvg(svg) : undefined;
  };

  // Events: registered once, routed to whatever MobileNav's handlers are now.
  useEffect(() => {
    const subs: Promise<PluginListenerHandle>[] = [
      GlassBar.addListener("tab", (e) => handlers.current.onTab(e.key)),
      GlassBar.addListener("ask", () => handlers.current.onAsk()),
      GlassBar.addListener("fold", () => handlers.current.onFold()),
      GlassBar.addListener("attach", () => handlers.current.onAttach()),
      GlassBar.addListener("mic", () => handlers.current.onMic()),
      GlassBar.addListener("submit", (e) => handlers.current.onSubmit(e.text)),
      GlassBar.addListener("draft", (e) => {
        nativeDraft.current = e.text;
        handlers.current.onDraft(e.text);
      }),
      GlassBar.addListener("focus", () => handlers.current.onFocus()),
      GlassBar.addListener("blur", () => handlers.current.onBlur()),
    ];
    return () => {
      subs.forEach((s) => void s.then((h) => h.remove()));
      void GlassBar.setMode({ mode: "hidden", animated: false });
    };
  }, []);

  // The tab set. Keyed on what the bar actually draws, so a re-render that
  // changes nothing sends nothing.
  const tabsKey = JSON.stringify([
    props.left.map((t) => [t.key, t.label]),
    props.right.map((t) => (t ? [t.key, t.label] : null)),
    props.ask,
    props.dark,
  ]);
  // The last configuration built, so a bar put away before it could be sent
  // still gets it when it comes back.
  const configRef = useRef<GlassBarConfig | null>(null);
  useEffect(() => {
    const toTab = (t: NativeTab): GlassBarTab | null => {
      const icon = glyph(`tab-${t.key}`);
      return icon ? { key: t.key, label: t.label, icon } : null;
    };
    const glyphs = Object.fromEntries(
      COMPOSER_ICONS.map(([name]) => [name, glyph(name)]).filter(([, g]) => g),
    ) as Record<string, Glyph>;
    configRef.current = {
      left: props.left.map(toTab).filter((t): t is GlassBarTab => !!t),
      right: props.right.map((t) => (t ? toTab(t) : null)),
      ask: props.ask,
      active: props.active,
      dark: props.dark,
      glyphs: { ...glyphs, ask: askMarkGlyph() } as Record<"ask" | "plus" | "x" | "mic" | "send" | "stop" | "loader", Glyph>,
    };
    // Configuring a hidden bar shows it, so not while it is put away or
    // covered: the restore (or the next mount) sends it instead.
    if (nativeBarHeld()) return;
    void GlassBar.configure(configRef.current);
    // tabsKey stands for left/right/ask/dark; the glyphs are read from the
    // DOM those same props just rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsKey]);

  useEffect(() => {
    void GlassBar.setActive({ key: props.active });
  }, [props.active]);

  // A field on the PAGE has focus (a job's notes, a quote line): the bar steps
  // aside for the keyboard, as the web bar does on `keyboard-open`. Read from
  // focus rather than keyboard geometry, which the Android WebView cannot
  // report (mobile-webview.md). The native composer's own field is not in the
  // DOM, so it never trips this.
  const [pageEditing, setPageEditing] = useState(false);
  useEffect(() => {
    const editable = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.isContentEditable || el.tagName === "TEXTAREA" ||
        (el.tagName === "INPUT" && !["button", "checkbox", "radio", "submit", "range", "color", "file"].includes((el as HTMLInputElement).type)));
    const onIn = (e: FocusEvent) => { if (editable(e.target)) setPageEditing(true); };
    const onOut = (e: FocusEvent) => { if (editable(e.target) && !editable(e.relatedTarget)) setPageEditing(false); };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  const mode: GlassBarMode = props.mode === "tabs" && pageEditing ? "hidden" : props.mode;

  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
    if (nativeBarHeld()) return;
    void GlassBar.setMode({ mode, animated: true });
  }, [mode]);

  // The page is going — a form POST (signing out), a reload, a hard link. The
  // bar is native and survives it, and React never unmounts this on the way
  // out, so hide it now rather than leave it over the next page until that
  // page's JS boots (MobileNav hides it again there). A page restored from the
  // back-forward cache puts it back as it was.
  useEffect(() => {
    const onHide = () => { void GlassBar.setMode({ mode: "hidden", animated: false }); };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted && !nativeBarHeld()) void GlassBar.setMode({ mode: modeRef.current, animated: false });
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onShow);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onShow);
    };
  }, []);

  // AppLock puts the bar away when the app goes to the background with the
  // lock on (chrome-cover.ts), and asks for it back when the return does not
  // lock. Back exactly as it is NOW — anything that changed while it was away
  // (a mount, a new tab set, the composer opening) was held, not sent.
  useEffect(() => onNativeBarRestore(() => {
    if (configRef.current) void GlassBar.configure(configRef.current);
    void GlassBar.setMode({ mode: modeRef.current, animated: false });
  }), []);

  const foldKey = props.foldIcon.displayName ?? props.foldIcon.name;
  useEffect(() => {
    const foldIcon = glyph("fold");
    if (!foldIcon) return;
    const composer: GlassBarComposer = {
      placeholder: props.placeholder,
      mic: props.mic,
      attachOpen: props.attachOpen,
      dimmed: props.dimmed,
      foldIcon,
      fold: props.fold,
      onDark: props.onDark,
    };
    void GlassBar.setComposer(composer);
    // foldKey stands for the fold icon, read from the DOM it just rendered.
  }, [props.placeholder, props.mic, props.attachOpen, props.dimmed, props.fold, props.onDark, foldKey]);

  useEffect(() => {
    if (props.draft === nativeDraft.current) return;
    nativeDraft.current = props.draft;
    void GlassBar.setDraft({ text: props.draft });
  }, [props.draft]);

  // The icons the native bar draws, rendered once here and read back as
  // outlines — so it draws exactly the icon the web would.
  const tabs = [...props.left, ...props.right.filter((t): t is NativeTab => !!t)];
  return (
    <div ref={glyphBox} hidden aria-hidden="true">
      {tabs.map((t) => (
        <span key={t.key} data-glyph={`tab-${t.key}`}>
          <Icon icon={t.icon} size={24} strokeWidth={2} />
        </span>
      ))}
      <span data-glyph="fold">
        <Icon icon={props.foldIcon} size={24} strokeWidth={2} />
      </span>
      {COMPOSER_ICONS.map(([name, icon, filled]) => (
        <span key={name} data-glyph={name}>
          <Icon icon={icon} size={24} strokeWidth={2} {...(filled ? { fill: "currentColor" } : {})} />
        </span>
      ))}
    </div>
  );
}
